
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { FeedbackReport, FeedbackReportStatus, FEEDBACK_REPORT_COMMENT_MAX_LENGTH } from '@run-diffusion/shared';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import NumberField from '@/components/base/NumberField.vue';
import { getShowTextCounterAt } from '@/utils/fieldValidationUtils';
import { commentLengthRule, STATUS_LABELS, STATUS_NOTE_COPY, StatusNoteCopy } from './feedbackReports';
import { findReportByNumber } from './findReport';

export default Vue.extend({
	name: 'FeedbackStatusDialog',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		// the status being set, or null while closed
		to: { type: String as PropType<FeedbackReportStatus | null>, default: null },
		// the status the report had when the dialog opened
		from: { type: String as PropType<FeedbackReportStatus | null>, default: null },
		// the loaded reports, to show the one a duplicate points at
		reports: { type: Array as PropType<FeedbackReport[]>, default: () => [] },
		saving: { type: Boolean, default: false },
	},
	data () {
		return {
			MAX: FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
			showCounterAt: getShowTextCounterAt(FEEDBACK_REPORT_COMMENT_MAX_LENGTH),
			note: '',
			duplicateOf: null as number | null,
			formValid: true,
			isMac: /Mac|iPhone|iPad/.test(navigator.platform || ''),
			statusLabels: STATUS_LABELS,
			// the report a duplicate names, when the list doesn't hold it: looked up by its number
			lookup: null as { number: number, report: FeedbackReport | null, failed: boolean } | null,
			lookupTimer: null as number | null,
			// the number a lookup is under way for, so another isn't started for it
			lookupPending: null as number | null,
		};
	},
	computed: {
		copy (): StatusNoteCopy {
			return (this.to && STATUS_NOTE_COPY[this.to]) || STATUS_NOTE_COPY.wontfix;
		},
		title (): string {
			return this.copy.title;
		},
		help (): string {
			return this.copy.help;
		},
		noteLabel (): string {
			return this.copy.noteLabel;
		},
		confirmText (): string {
			return this.copy.confirm;
		},
		excerpt (): string {
			const text: string = this.report.happened || '';
			return text.length > 60 ? text.slice(0, 60).trim() + '...' : text;
		},
		noteRules (): Array<(value: string) => boolean | string> {
			const rules: Array<(value: string) => boolean | string> = [commentLengthRule];
			if (this.copy.noteRequired) rules.push((value: string) => !!_trim(value) || 'Required');
			return rules;
		},
		duplicateRule (): (value: number | null) => boolean | string {
			return (value: number | null): boolean | string => {
				if (!value) return 'The report number';
				if (value === this.report.number) return 'A report can\'t repeat itself';
				return true;
			};
		},
		original (): FeedbackReport | null {
			if (!this.duplicateOf || this.duplicateOf === this.report.number) return null;
			const loaded: FeedbackReport | undefined = this.reports.find((report: FeedbackReport) => report.number === this.duplicateOf);
			if (loaded) return loaded;
			return this.lookup && this.lookup.number === this.duplicateOf ? this.lookup.report : null;
		},
		// what the dialog knows of a number it can't show yet
		lookupNote (): string {
			if (!this.duplicateOf || this.duplicateOf === this.report.number || this.original) return '';
			if (!this.lookup || this.lookup.number !== this.duplicateOf) return `Looking for #${this.duplicateOf}...`;
			if (this.lookup.failed) return `Couldn't look up #${this.duplicateOf}. Check your connection.`;
			return `There's no report #${this.duplicateOf}. Check the number.`;
		},
		lookupFailed (): boolean {
			return !this.original && !!this.lookup && this.lookup.number === this.duplicateOf && this.lookup.failed;
		},
		// someone else changed the status after the dialog opened
		moved (): boolean {
			return !!this.from && this.report.status !== this.from;
		},
		canConfirm (): boolean {
			if (this.moved) return false;
			const note: string = _trim(this.note || '');
			if (note.length > FEEDBACK_REPORT_COMMENT_MAX_LENGTH) return false;
			if (this.copy.noteRequired && !note) return false;
			if (this.to === 'duplicate' && !this.original) return false;
			return true;
		},
	},
	watch: {
		// once the number has stopped changing
		duplicateOf () {
			this.lookUp(350);
		},
		// the report it names may have been pushed out of the list by newer ones
		reports () {
			if (!this.original) this.lookUp(0);
		},
		to (to: FeedbackReportStatus | null) {
			if (!to) {
				// closed: nothing more is looked up for it
				if (this.lookupTimer) window.clearTimeout(this.lookupTimer);
				this.lookupTimer = null;
				this.duplicateOf = null;
				this.lookup = null;
				return;
			}
			this.note = '';
			this.duplicateOf = null;
			this.lookup = null;
			// after the dialog has opened and taken focus itself
			window.setTimeout(() => {
				if (this.to !== to) return;
				const field = (to === 'duplicate' ? this.$refs.duplicateOf : this.$refs.note) as Vue | undefined;
				const input: HTMLElement | null = field ? field.$el.querySelector('input, textarea') : null;
				if (input) input.focus();
			}, 250);
		},
	},
	mounted () {
		window.addEventListener('online', this.onOnline);
	},
	beforeDestroy () {
		window.removeEventListener('online', this.onOnline);
		if (this.lookupTimer) window.clearTimeout(this.lookupTimer);
	},
	methods: {
		// Looks the number up after `delay`, while the dialog is open for a duplicate, unless the list
		// or an earlier lookup has its report or a lookup for it is under way. One that wasn't found is
		// looked up again: it may have been sent since, or be reachable once online.
		lookUp (delay: number) {
			if (this.lookupTimer) window.clearTimeout(this.lookupTimer);
			this.lookupTimer = null;
			const n: number | null = this.duplicateOf;
			if (this.to !== 'duplicate' || !n || n === this.report.number || this.original || this.lookupPending === n) return;
			this.lookupTimer = window.setTimeout(async () => {
				this.lookupTimer = null;
				this.lookupPending = n;
				// a retry after a failure says it is looking again
				if (this.lookup && this.lookup.number === n && this.lookup.failed) this.lookup = null;
				try {
					const found: FeedbackReport | null = await findReportByNumber(n);
					if (this.duplicateOf === n) this.lookup = { number: n, report: found, failed: false };
				} catch (e) {
					console.error('[feedback] failed to look up report', n, e);
					if (this.duplicateOf === n) this.lookup = { number: n, report: null, failed: true };
				} finally {
					if (this.lookupPending === n) this.lookupPending = null;
				}
			}, delay);
		},
		// Back online: a lookup that failed for want of a connection tries again, once Firestore has
		// had a moment to reconnect.
		onOnline () {
			if (this.lookupFailed) this.lookUp(1500);
		},
		onInput (open: boolean) {
			if (!open) this.cancel();
		},
		cancel () {
			if (!this.saving) this.$emit('cancel');
		},
		confirm () {
			if (!this.canConfirm || this.saving) return;
			this.$emit('confirm', { note: _trim(this.note || ''), duplicateOf: this.to === 'duplicate' ? this.duplicateOf : null });
		},
	},
	components: {
		ActionsIsland,
		DialogContent,
		DialogOrBottomSheet,
		NumberField,
	},
});
